import { Link, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import detercamLogo from '@/assets/detercam-logo.png'
import { cn, avatarColor } from '@/lib/utils'
import { useUIStore } from '@/store/uiStore'
import { useAuthStore } from '@/store/authStore'
import { queryClient } from '@/main'
import { logoutAndEndSession } from '@/lib/dashboardLogout'
import { useG100ConsoleCameras } from '@/hooks/useG100Console'
import { Button } from '@/components/ui/button'
import {
  Camera,
  LayoutDashboard,
  Settings,
  Users,
  FolderOpen,
  ChevronLeft,
  ChevronRight,
  LogOut,
  Activity,
  FlaskConical,
  PenTool,
  ScrollText,
  SmartphoneNfc,
  Video,
  SlidersHorizontal,
  Star,
  Terminal,
} from 'lucide-react'

interface NavItem {
  title: string
  href: string
  icon: React.ReactNode
  disabled?: boolean
}

export function Sidebar() {
  const { t } = useTranslation('common')
  const location = useLocation()
  const { sidebarCollapsed, setSidebarCollapsed, sidebarOpen, setSidebarOpen } = useUIStore()
  const { user } = useAuthStore()
  // #309 — the G100 Test Console exists only where it is on (the list answers) and
  // only for a user with a G100 camera to test.
  const consoleCameras = useG100ConsoleCameras()
  const showG100Console = (consoleCameras.data?.results.length ?? 0) > 0

  const mainNavItems: NavItem[] = [
    { title: t('nav.dashboard'), href: '/', icon: <LayoutDashboard className="h-5 w-5" /> },
    { title: t('nav.cameras'), href: '/cameras', icon: <Camera className="h-5 w-5" /> },
    { title: t('nav.events'), href: '/events', icon: <Activity className="h-5 w-5" /> },
    { title: t('nav.favourites'), href: '/favourites', icon: <Star className="h-5 w-5" /> },
    { title: t('nav.groups'), href: '/groups', icon: <FolderOpen className="h-5 w-5" /> },
  ]

  const settingsNavItem: NavItem = {
    title: t('nav.settings'),
    href: '/settings',
    icon: <Settings className="h-5 w-5" />,
  }

  const usersNavItem: NavItem = {
    title: t('nav.users'),
    href: '/users',
    icon: <Users className="h-5 w-5" />,
  }

  const testCamerasNavItem: NavItem = {
    title: t('nav.testCameras'),
    href: '/test-cameras',
    icon: <FlaskConical className="h-5 w-5" />,
  }

  const annotationsNavItem: NavItem = {
    title: t('nav.annotations'),
    href: '/annotations',
    icon: <PenTool className="h-5 w-5" />,
  }

  const cameraLogsNavItem: NavItem = {
    title: t('nav.cameraLogs'),
    href: '/camera-logs',
    icon: <ScrollText className="h-5 w-5" />,
  }

  // #224 — G100 communication log, superuser only
  const g100LogsNavItem: NavItem = {
    title: t('nav.g100Logs'),
    href: '/g100-logs',
    icon: <ScrollText className="h-5 w-5" />,
  }

  const g100ConsoleNavItem: NavItem = {
    title: t('nav.g100Console'),
    href: '/g100-console',
    icon: <Terminal className="h-5 w-5" />,
  }

  const simMismatchesNavItem: NavItem = {
    title: 'SIM mismatches',
    href: '/sim-mismatches',
    icon: <SmartphoneNfc className="h-5 w-5" />,
  }

  const allCamerasNavItem: NavItem = {
    title: 'All Cameras',
    href: '/all-cameras',
    icon: <Video className="h-5 w-5" />,
  }

  const detectionAccuracyNavItem: NavItem = {
    title: t('sidebar.detectionAccuracy'),
    href: '/detection-accuracy',
    icon: <SlidersHorizontal className="h-5 w-5" />,
  }

  const handleLogout = () => {
    queryClient.clear()
    logoutAndEndSession()
  }

  const initials = user?.username
    ? user.username.slice(0, 2).toUpperCase()
    : '??'
  const avatarBg = user?.username ? avatarColor(user.username) : 'bg-muted'

  const navLink = (item: NavItem) => {
    const isActive = location.pathname === item.href
    return (
      <Link
        key={item.href}
        to={item.href}
        onClick={() => setSidebarOpen(false)}
        className={cn(
          'group relative flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-all duration-150',
          isActive
            ? 'bg-primary/10 text-primary'
            : 'text-muted-foreground hover:bg-muted hover:text-foreground',
          sidebarCollapsed && 'justify-center px-2'
        )}
        title={sidebarCollapsed ? item.title : undefined}
      >
        {/* Active left accent bar */}
        {isActive && (
          <span className="absolute left-0 top-1/2 h-5 w-0.5 -translate-y-1/2 rounded-full bg-primary" />
        )}
        <span className={cn(
          'flex-shrink-0 transition-colors',
          isActive ? 'text-primary' : 'text-muted-foreground group-hover:text-foreground'
        )}>
          {item.icon}
        </span>
        {!sidebarCollapsed && <span>{item.title}</span>}
      </Link>
    )
  }

  const disabledLink = (item: NavItem) => (
    <span
      key={item.href}
      className={cn(
        'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium',
        'text-muted-foreground/35 cursor-not-allowed select-none',
        sidebarCollapsed && 'justify-center px-2'
      )}
      title={sidebarCollapsed ? `${item.title} (${t('sidebar.comingSoon')})` : undefined}
    >
      {item.icon}
      {!sidebarCollapsed && (
        <span className="flex items-center gap-2">
          {item.title}
          <span className="rounded-sm bg-muted px-1 py-0.5 text-[9px] uppercase tracking-wide font-semibold text-muted-foreground/50">
            {t('sidebar.comingSoon')}
          </span>
        </span>
      )}
    </span>
  )

  return (
    <aside
      className={cn(
        'fixed left-0 top-0 z-50 h-[100dvh]',
        // Tinted sidebar background — slightly distinct from main content
        'bg-background lg:bg-muted/40 lg:dark:bg-muted/20 border-r border-border/60',
        sidebarCollapsed ? 'w-16' : 'w-64',
        // Desktop: animate width only, no transform
        'lg:transition-[width] lg:duration-300',
        // Mobile: slide in/out; desktop always visible
        !sidebarOpen && 'max-lg:-translate-x-full',
        'max-lg:transition-transform max-lg:duration-300'
      )}
    >
      <div className="flex h-full flex-col overflow-hidden">

        {/* Logo */}
        <div className="flex h-14 sm:h-16 items-center border-b border-border/60 px-2 sm:px-3 flex-shrink-0">
          {sidebarCollapsed ? (
            /* Collapsed: just the collapse toggle centred */
            <div className="flex flex-1 items-center justify-center">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                className="hidden lg:flex h-7 w-7 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              >
                <ChevronRight className="h-3.5 w-3.5" />
              </Button>
            </div>
          ) : (
            /* Expanded: logo + collapse toggle */
            <>
              <Link to="/" className="flex flex-1 items-center justify-center" onClick={() => setSidebarOpen(false)}>
                <img
                  src={detercamLogo}
                  alt="DeterCam"
                  className="h-9 sm:h-10 w-auto max-w-full object-contain block"
                />
              </Link>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                className="hidden lg:flex flex-shrink-0 h-7 w-7 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              >
                <ChevronLeft className="h-3.5 w-3.5" />
              </Button>
            </>
          )}
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto p-2 space-y-0.5">

          {/* Main nav */}
          <div className="space-y-0.5">
            {mainNavItems.map(item => item.disabled ? disabledLink(item) : navLink(item))}
            {showG100Console && navLink(g100ConsoleNavItem)}
          </div>

          {/* Admin section */}
          <div className="pt-4 space-y-0.5">
            {!sidebarCollapsed && (
              <p className="px-3 pb-1 text-micro text-muted-foreground/60">
                {t('sidebar.admin')}
              </p>
            )}
            {sidebarCollapsed && <div className="my-2 border-t border-border/40" />}
            {user?.is_superuser && navLink(allCamerasNavItem)}
            {user?.is_superuser && navLink(detectionAccuracyNavItem)}
            {user?.is_superuser && navLink(usersNavItem)}
            {user?.is_superuser && navLink(testCamerasNavItem)}
            {user?.is_superuser && navLink(annotationsNavItem)}
            {user?.is_superuser && navLink(cameraLogsNavItem)}
            {user?.is_superuser && navLink(g100LogsNavItem)}
            {user?.is_superuser && navLink(simMismatchesNavItem)}
            {navLink(settingsNavItem)}
          </div>
        </nav>

        {/* User badge & Logout */}
        <div className="border-t border-border/60 p-2 flex-shrink-0">
          {!sidebarCollapsed && user ? (
            <div className="mb-1.5 flex items-center gap-2.5 rounded-lg px-2 py-2">
              {/* Avatar */}
              <div className={cn(
                'flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-[11px] font-bold text-white',
                avatarBg
              )}>
                {initials}
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-body font-medium leading-tight">{user.username}</p>
                <p className="truncate text-caption text-muted-foreground leading-tight">{user.email}</p>
              </div>
            </div>
          ) : sidebarCollapsed && user ? (
            <div className="mb-1.5 flex justify-center">
              <div className={cn(
                'flex h-7 w-7 items-center justify-center rounded-full text-[11px] font-bold text-white',
                avatarBg
              )} title={user.username}>
                {initials}
              </div>
            </div>
          ) : null}

          <Button
            variant="ghost"
            className={cn(
              'w-full justify-start gap-2.5 text-sm text-muted-foreground hover:text-destructive hover:bg-destructive/8 transition-colors',
              sidebarCollapsed && 'justify-center px-2'
            )}
            onClick={handleLogout}
            title={sidebarCollapsed ? t('sidebar.logout') : undefined}
          >
            <LogOut className="h-4 w-4 flex-shrink-0" />
            {!sidebarCollapsed && <span>{t('sidebar.signOut')}</span>}
          </Button>
        </div>
      </div>
    </aside>
  )
}
